import React from 'react';
import { API_URL } from '../../api/client';
import { DbEntry } from '../../models/DbEntry';

interface DbSearchResultsTableProps {
   value: Array<DbEntry>;
}

const DbSearchResultsTable = (props: DbSearchResultsTableProps): React.JSX.Element => {
   const render_chain = (chain: string): React.JSX.Element => {
      return <span className="chain at ">chain{chain}</span>;
   };

   const render_chains = (chains: Array<string>): React.JSX.Element => {
      return (
         <>
            {chains.map((chain: string, index: number) => (
               <React.Fragment key={chain}>
                  {render_chain(chain)}
                  {index < chains.length - 1 ? ', ' : ''}
               </React.Fragment>
            ))}
         </>
      );
   };

   const render_uniprot_id = (uniprot_id: string): React.JSX.Element => {
      return (
         <a href={`https://www.uniprot.org/uniprotkb/${uniprot_id}/entry`} target="_blank" title="go to UniProtKB">
            {uniprot_id}
         </a>
      );
   };

   const render_uniprot_ids = (uniprot_ids: Array<string>): React.JSX.Element => {
      return (
         <>
            {uniprot_ids.map((uniprot_id: string) => (
               <React.Fragment key={uniprot_id}>{render_uniprot_id(uniprot_id)}</React.Fragment>
            ))}
         </>
      );
   };

   return (
      <table id="db-search-results-table">
         <thead>
            <tr>
               <th title="DB Entry ID. Corresponding AHoJ Query is displayed in the tooltip.">Entry</th>
               <th title="PDB Chain that was used as a target of AHoJ search or multiple chains in case that ligand is on the interface.">
                  Target PDB Chain(s)
               </th>
               <th title="UniProt ID of Target PDB Chain(s)">UniProtID(s)</th>
               <th title="Target chain resolution in Å. (Experimental method is shown on hover)">Resolution</th>
               <th title="Ligand of Target PDB Chain. Found chains are marked as Apo/Holo with respect to the binding site defined by this ligand.">
                  Target Ligand
               </th>
               <th title="Total Solvent Accessible Surface Area of the binding site defined by Target Ligand (Å²). Computed for binding residues, excluding ligand or solvent molecules.">
                  SASA
               </th>
               <th title="Total SASA, divided by the number of binding residues (Å²).">avg. SASA </th>
               <th title="Num. of found matching Apo sites">Apo Sites</th>
               <th title="Num. of found matching Holo sites">Holo Sites</th>
               <th style={{ textAlign: 'right' }}></th>
            </tr>
         </thead>
         <tbody>
            {props.value.map((entry: DbEntry) => (
               <tr key={entry.entry_key}>
                  <td>
                     <code className="codeid">
                        <a href={`db/entry/${entry.entry_key}`} title={`AHoJ Query: ${entry.query}`}>
                           {entry.entry_key}
                        </a>
                     </code>
                  </td>
                  <td>
                     <a
                        href={`https://www.rcsb.org/structure/${entry.target_pdb_id}`}
                        target="_blank"
                        title="go to RCSB"
                     >
                        {entry.target_pdb_id}
                     </a>{' '}
                     / {render_chains(entry.target_chains)}
                  </td>
                  <td>{render_uniprot_ids(entry.target_uniprot_ids ?? [])}</td>
                  <td>
                     <span
                        title={`Experimental method: ${entry.target_experimental_method ? entry.target_experimental_method : 'N/A'}`}
                     >
                        {entry.target_resolution}
                     </span>
                  </td>
                  <td>
                     <a
                        href={`https://www.rcsb.org/structure/${entry.target_pdb_id}#ligand_row_${entry.target_ligand}`}
                        target="_blank"
                        title="go to RCSB/ligand"
                     >
                        {entry.target_ligand}
                     </a>
                     (
                     <span title="ligand group" style={{ color: '#999' }}>
                        {entry.target_ligand_group}
                     </span>
                     )
                  </td>
                  <td>{entry.sasa}</td>
                  <td>{entry.sasa_avg}</td>
                  <td>{entry.num_apo_chains}</td>
                  <td>{entry.num_holo_chains}</td>
                  <td className="action-buttons">
                     <a href={`db/entry/${entry.entry_key}`}>
                        <button className="btn btn-primary">View</button>
                     </a>
                     {entry.result_zip_url && (
                        <a href={`${API_URL}/${entry.result_zip_url}`}>
                           <button className="btn btn-primary">Download</button>
                        </a>
                     )}
                  </td>
               </tr>
            ))}
         </tbody>
      </table>
   );
};
export default DbSearchResultsTable;
